import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fetchPriceVarianceGrouped, type PriceVarianceFilters, type VarianceGroupRow } from "../../api/reports";
import DataRecovery from "../../components/DataRecovery";
import Spinner from "../../components/Spinner";
import { cleanDescription } from "../../lib/description";
import { filterColor } from "../../lib/filterColors";
import { formatEuro, formatSignedPercent, toNumber } from "../../lib/format";
import ShareDonutCard, { type ShareRow } from "./ShareDonutCard";

const TOP_COUNT = 10;
type TopSort = "overpay" | "deviation";

function toShareRows(rows: readonly VarianceGroupRow[] | undefined, axis: "supplier" | "manufacturer"): ShareRow[] {
  return (rows ?? []).flatMap((row) => {
    const id = axis === "supplier" ? row.supplier : row.manufacturer_id;
    if (!id) return [];
    const label = axis === "supplier" ? id : row.manufacturer_name ?? id;
    return [{ id, label, value: Math.max(0, toNumber(row.total_overpay) ?? 0) }];
  });
}

/**
 * Reiter „Mehrkosten Top 10“ (Beta-Feedback 2026-10-07, Entwurf E): wo die
 * Mehrkosten liegen – nach Großhändler und Hersteller – und welche zehn
 * Artikel, mit Mehrkosten und höchster Abweichung nebeneinander. Alles folgt
 * den Filtern oben.
 */
export default function OverpayTop10Tab({
  filters,
  supplierRows,
  supplierLoading,
  supplierError,
  manufacturerRows,
  manufacturerLoading,
  manufacturerError,
  onFilter,
  positionsHref,
}: {
  filters: PriceVarianceFilters;
  supplierRows?: VarianceGroupRow[];
  supplierLoading: boolean;
  supplierError: boolean;
  manufacturerRows?: VarianceGroupRow[];
  manufacturerLoading: boolean;
  manufacturerError: boolean;
  onFilter: (key: "supplier" | "manufacturer_id", id: string) => void;
  positionsHref: string;
}) {
  const [sort, setSort] = useState<TopSort>("overpay");
  const articlesQuery = useQuery({
    queryKey: ["reports", "analysis-top-articles", filters, sort],
    queryFn: () =>
      fetchPriceVarianceGrouped({
        ...filters,
        group_by: "article",
        sort: sort === "overpay" ? "overpay_desc" : "max_delta_desc",
      }),
  });
  const rows = useMemo(
    () => (articlesQuery.data?.items ?? []).filter((row) => (toNumber(row.total_overpay) ?? 0) > 0).slice(0, TOP_COUNT),
    [articlesQuery.data],
  );
  const maxOverpay = Math.max(0, ...rows.map((row) => toNumber(row.total_overpay) ?? 0));
  const maxDelta = Math.max(0, ...rows.map((row) => toNumber(row.max_delta_pct) ?? 0));

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <ShareDonutCard
          title="Mehrkosten nach Großhändler"
          rows={toShareRows(supplierRows, "supplier")}
          colorFor={(id) => filterColor("supplier", id)}
          isLoading={supplierLoading}
          isError={supplierError}
          emptyText="Keine Mehrkosten in dieser Auswahl."
          selected={filters.supplier}
          onSelect={(id) => onFilter("supplier", id)}
          restLabel={(count) => `Übrige ${count} Großhändler`}
          showBars={false}
          size={112}
        />
        <ShareDonutCard
          title="Mehrkosten nach Hersteller"
          rows={toShareRows(manufacturerRows, "manufacturer")}
          colorFor={(id) => filterColor("manufacturer", id)}
          isLoading={manufacturerLoading}
          isError={manufacturerError}
          emptyText="Keine Mehrkosten in dieser Auswahl."
          selected={filters.manufacturer_id}
          onSelect={(id) => onFilter("manufacturer_id", id)}
          restLabel={(count) => `Übrige ${count} Hersteller`}
          showBars={false}
          size={132}
        />
      </div>

      <section aria-label="Top 10 Artikel" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Top 10 Artikel</h2>
            <p className="mt-0.5 text-xs text-slate-600">Mehrkosten und höchste Abweichung je Artikel</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-slate-600">Sortiert nach</span>
            <div role="group" aria-label="Top 10 sortieren" className="inline-flex overflow-hidden rounded-md border border-slate-300">
              {([["overpay", "Mehrkosten €"], ["deviation", "Abweichung %"]] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={sort === key}
                  onClick={() => setSort(key)}
                  className={`h-8 px-3 ${sort === key ? "bg-emerald-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Link to={positionsHref} className="font-medium text-emerald-700 hover:underline">Alle Mehrkosten →</Link>
          </div>
        </div>

        {articlesQuery.isError ? (
          <div className="mt-3">
            <DataRecovery error={articlesQuery.error} subject="Die Top 10" onRetry={() => void articlesQuery.refetch()} />
          </div>
        ) : articlesQuery.isPending ? (
          <div className="flex h-40 items-center justify-center"><Spinner size="sm" label="Lade Top 10 …" /></div>
        ) : rows.length === 0 ? (
          <p className="mt-3 text-sm text-slate-600">In dieser Auswahl gibt es keine Mehrkosten.</p>
        ) : (
          <ol className="mt-3" data-testid="analysis-top10">
            <li aria-hidden="true" className="hidden grid-cols-[1.5rem_minmax(0,1fr)_14rem_11rem_5rem] gap-4 border-b border-slate-100 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 md:grid">
              <span />
              <span>Artikel</span>
              <span className="text-right">Mehrkosten</span>
              <span className="text-right">Höchste Abweichung</span>
              <span className="text-right">Käufe</span>
            </li>
            {rows.map((row, index) => {
              const overpay = toNumber(row.total_overpay) ?? 0;
              const delta = toNumber(row.max_delta_pct);
              const manufacturer = row.manufacturer_id ?? "";
              const color = manufacturer ? filterColor("manufacturer", manufacturer) : "#64748b";
              const name = cleanDescription(row.description ?? "") || row.primary_article_code || "Artikel";
              const purchases = row.overpay_line_count ?? row.line_count ?? 0;
              return (
                <li key={row.article_group_key ?? index} data-top10-row className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 border-t border-slate-100 py-2.5 first:border-t-0 md:grid-cols-[1.5rem_minmax(0,1fr)_14rem_11rem_5rem]">
                  <span className="row-span-2 text-right text-xs tabular-nums text-slate-500 md:row-span-1">{index + 1}</span>
                  <div className="row-span-2 min-w-0 md:row-span-1">
                    {row.article_group_key ? (
                      <Link to={`/articles/${encodeURIComponent(row.article_group_key)}`} title={name} className="block truncate text-sm font-medium text-slate-900 hover:text-emerald-700 hover:underline">{name}</Link>
                    ) : (
                      <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
                    )}
                    <span className="block truncate text-xs text-slate-600">
                      <span className="font-mono">{row.primary_article_code ?? "–"}</span>
                      {row.manufacturer_name ? ` · ${row.manufacturer_name}` : ""}
                    </span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <span aria-hidden="true" className="hidden h-2 w-28 overflow-hidden rounded-full bg-slate-100 md:block">
                      <span className="block h-full rounded-full" style={{ width: `${maxOverpay > 0 ? (overpay / maxOverpay) * 100 : 0}%`, backgroundColor: color }} />
                    </span>
                    <span className="text-right text-sm font-semibold tabular-nums text-slate-900">{formatEuro(overpay)}</span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <span aria-hidden="true" className="hidden h-2 w-20 overflow-hidden rounded-full bg-slate-100 md:block">
                      <span className="block h-full rounded-full bg-rose-300" style={{ width: `${maxDelta > 0 && delta !== null ? (delta / maxDelta) * 100 : 0}%` }} />
                    </span>
                    <span className="whitespace-nowrap text-right text-sm font-medium tabular-nums text-rose-700">{delta !== null ? formatSignedPercent(delta) : "–"}</span>
                  </div>
                  <span className="hidden text-right text-xs tabular-nums text-slate-600 md:block">
                    {purchases} {purchases === 1 ? "Kauf" : "Käufe"}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
